import * as React from 'react';
import Papa from 'papaparse';
import { Loader } from 'Src/components/Loader/Loader';
import { superAdminGet, superAdminPost } from 'Src/helpers/service/superadmin/api';
import { getSuperAdminSchools } from 'Src/helpers/service/superadmin/schools';

interface ActivatedStudent {
  id: string;
  email?: string;
  token?: string;
}

interface StudentRow {
  studentId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  [key: string]: any;
}

function downloadCsv(data: ActivatedStudent[], filename: string) {
  const csv = Papa.unparse(data);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

interface Props {}

interface State {
  schoolSearch: string;
  schoolId: string;
  schoolOptions: Array<{ id: string; name: string }>;
  isSearchingSchools: boolean;
  csvRows: StudentRow[];
  csvErrors: string[];
  isActivating: boolean;
  activatedStudents: ActivatedStudent[] | null;
  errorMessage: string;
}

export class SuperAdminActivateStudents extends React.Component<Props, State> {
  schoolSearchTimer: any;

  constructor(props: Props) {
    super(props);
    this.state = {
      schoolSearch: '',
      schoolId: '',
      schoolOptions: [],
      isSearchingSchools: false,
      csvRows: [],
      csvErrors: [],
      isActivating: false,
      activatedStudents: null,
      errorMessage: ''
    };
  }

  onSchoolSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    this.setState({ schoolSearch: val, schoolId: '', csvRows: [], csvErrors: [], activatedStudents: null });

    clearTimeout(this.schoolSearchTimer);
    if (val.trim().length < 2) {
      this.setState({ schoolOptions: [] });
      return;
    }

    this.schoolSearchTimer = setTimeout(() => {
      this.setState({ isSearchingSchools: true });
      getSuperAdminSchools({ where: { name: { like: val, options: 'i' } }, limit: 10, skip: 0 })
        .then(schools =>
          this.setState({
            isSearchingSchools: false,
            schoolOptions: schools.map(s => ({ id: s.id, name: s.name || '' }))
          })
        )
        .catch(() => this.setState({ isSearchingSchools: false }));
    }, 300);
  };

  onSchoolSelect = (school: { id: string; name: string }) => {
    this.setState({
      schoolId: school.id,
      schoolSearch: school.name,
      schoolOptions: [],
      csvRows: [],
      activatedStudents: null
    });
  };

  onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: result => {
        const errors = result.errors.map(err => `Row ${err.row}: ${err.message}`);
        const rows = result.data as StudentRow[];
        if (rows.length === 0) {
          errors.push('No data rows found in CSV.');
        }
        // Check for required column
        const firstRow = rows[0] || {};
        if (!firstRow.studentId && !firstRow['studentId']) {
          errors.push('Column "studentId" not found. CSV must have a "studentId" column.');
        }
        this.setState({ csvRows: rows, csvErrors: errors, activatedStudents: null });
      },
      error: err => {
        this.setState({ csvErrors: [err.message], csvRows: [] });
      }
    });
  };

  onActivate = () => {
    const { schoolId, csvRows } = this.state;
    this.setState({ isActivating: true, errorMessage: '', activatedStudents: null });

    superAdminPost(`/schools/${schoolId}/students/activate`, { students: csvRows })
      .then((response: ActivatedStudent[]) => {
        this.setState({ isActivating: false, activatedStudents: response });
        downloadCsv(response, 'activated_students.csv');
      })
      .catch(err => {
        console.error('Cannot activate students', err);
        this.setState({ isActivating: false, errorMessage: 'Could not activate students. Please try again.' });
      });
  };

  render() {
    const {
      schoolSearch,
      schoolId,
      schoolOptions,
      isSearchingSchools,
      csvRows,
      csvErrors,
      isActivating,
      activatedStudents,
      errorMessage
    } = this.state;

    return (
      <div>
        <div className="mb-4">
          <h4 className="mb-1">Activate students</h4>
          <div className="text-muted small">
            Upload a CSV with <code>studentId</code> and <code>email</code> columns to send activation emails.
          </div>
        </div>

        {/* School picker */}
        <div className="form-group" style={{ maxWidth: 400, position: 'relative' }}>
          <label className="small font-weight-bold">School</label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={schoolSearch}
            onChange={this.onSchoolSearchChange}
            placeholder="Type to search school…"
          />
          {schoolId && <small className="text-success">✓ {schoolSearch}</small>}
          {(schoolOptions.length > 0 || isSearchingSchools) && (
            <div
              style={{
                position: 'absolute',
                zIndex: 100,
                background: '#fff',
                border: '1px solid #ced4da',
                borderRadius: 4,
                width: '100%',
                maxHeight: 200,
                overflowY: 'auto'
              }}
            >
              {isSearchingSchools && <div className="p-2 text-muted small">Searching…</div>}
              {schoolOptions.map(s => (
                <div
                  key={s.id}
                  className="px-3 py-2 small"
                  style={{ cursor: 'pointer' }}
                  onMouseDown={() => this.onSchoolSelect(s)}
                >
                  {s.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* File upload — only after school selected */}
        {schoolId && (
          <div className="form-group" style={{ maxWidth: 400 }}>
            <label className="small font-weight-bold">CSV file</label>
            <input type="file" className="form-control-file" accept=".csv" onChange={this.onFileChange} />
          </div>
        )}

        {/* CSV errors */}
        {csvErrors.length > 0 && (
          <div className="alert alert-warning">
            <strong>CSV issues:</strong>
            <ul className="mb-0 mt-1">
              {csvErrors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Preview */}
        {csvRows.length > 0 && (
          <div className="mb-3">
            <div className="text-muted small mb-2">
              Ready to activate <strong>{csvRows.length}</strong> student{csvRows.length !== 1 ? 's' : ''}.
            </div>
            <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 4 }}>
              <table className="table table-sm table-bordered mb-0">
                <thead className="thead-light">
                  <tr>
                    {Object.keys(csvRows[0]).map(k => (
                      <th key={k} className="small">
                        {k}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {csvRows.slice(0, 10).map((row, i) => (
                    <tr key={i}>
                      {Object.keys(row).map((k, j) => (
                        <td key={j} className="small">
                          {String(row[k] != null ? row[k] : '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {csvRows.length > 10 && (
                    <tr>
                      <td colSpan={Object.keys(csvRows[0]).length} className="text-muted small text-center">
                        … and {csvRows.length - 10} more rows
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

        {/* Activate button */}
        {csvRows.length > 0 && !isActivating && (
          <button
            className="btn btn-primary btn-sm"
            onClick={this.onActivate}
            disabled={csvErrors.some(e => e.includes('"studentId"'))}
          >
            Activate {csvRows.length} student{csvRows.length !== 1 ? 's' : ''}
          </button>
        )}

        {isActivating && <Loader />}

        {/* Result */}
        {activatedStudents && (
          <div className="alert alert-success mt-3">
            ✓ {activatedStudents.length} student{activatedStudents.length !== 1 ? 's' : ''} activated. CSV with tokens
            has been downloaded automatically.
          </div>
        )}
      </div>
    );
  }
}

